<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const uiFlags = useMapGetter('bulkMessages/getUIFlags');
const allBulkMessages = useMapGetter('bulkMessages/getAllBulkMessages');

const isFetching = computed(() => uiFlags.value.isFetching);
const isSending = computed(() => uiFlags.value.isSending);

const showNewForm = ref(false);
const editingId = ref(null);

// Form state
const form = ref({
  title: '',
  channel_type: 'whatsapp',
  inbox_id: '',
  message: '',
  scheduled_at: '',
  recipient_type: 'all',
  label_ids: [],
  // Filtro de data (actividade dos contactos)
  date_from: '',
  date_to: '',
  // Delay entre mensagens (segundos)
  delay_min: 5,
  delay_max: 12,
});

// Editor state
const messageTextarea = ref(null);
const imageFileInput = ref(null);
const isUploadingImage = ref(false);
const showLinkDialog = ref(false);
const linkUrl = ref('');
const linkText = ref('');
const showImageUrlDialog = ref(false);
const imageUrlInput = ref('');

// Variáveis disponíveis para personalização
const availableVariables = [
  { key: '{{nome}}', label: 'Nome', description: 'Nome completo do contacto' },
  { key: '{{name}}', label: 'Name (EN)', description: 'Contact full name (English)' },
  { key: '{{email}}', label: 'Email', description: 'Endereço de email do contacto' },
  { key: '{{telefone}}', label: 'Telefone', description: 'Número de telefone do contacto' },
  { key: '{{phone}}', label: 'Phone (EN)', description: 'Contact phone number (English)' },
];

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const whatsappInboxes = computed(() =>
  inboxes.value.filter(i => i.channel_type === 'Channel::Whatsapp')
);
const emailInboxes = computed(() =>
  inboxes.value.filter(i => i.channel_type === 'Channel::Email')
);

const availableInboxes = computed(() => {
  if (form.value.channel_type === 'whatsapp') return whatsappInboxes.value;
  if (form.value.channel_type === 'email') return emailInboxes.value;
  return [];
});

// Opções de destinatários
const recipientOptions = computed(() => {
  const isWhatsapp = form.value.channel_type === 'whatsapp';
  const options = [
    { value: 'all', label: 'Todos os contactos', description: '' },
  ];
  if (isWhatsapp) {
    options.push(
      {
        value: 'window_inside',
        label: 'Dentro de 24h (gratuito)',
        description: 'Contactos que enviaram mensagem nas últimas 24h — sem custo de template',
      },
      {
        value: 'window_outside',
        label: 'Fora de 24h (pago)',
        description: 'Contactos fora da janela de 24h — requer template aprovado',
      }
    );
  }
  options.push({ value: 'label', label: 'Por etiqueta', description: '' });
  return options;
});

const selectedRecipientDescription = computed(() => {
  const opt = recipientOptions.value.find(o => o.value === form.value.recipient_type);
  return opt?.description || '';
});

const recipientLabel = type => {
  const map = {
    all: 'Todos',
    window_inside: 'Dentro de 24h',
    window_outside: 'Fora de 24h',
    label: 'Por etiqueta',
  };
  return map[type] || type;
};

const statusLabel = status => {
  const map = {
    draft: 'Rascunho',
    scheduled: 'Agendado',
    running: 'A enviar...',
    completed: 'Concluído',
    failed: 'Falhou',
  };
  return map[status] || status;
};

const statusClass = status => {
  const map = {
    draft: 'bg-yellow-100 text-yellow-800',
    scheduled: 'bg-blue-100 text-blue-800',
    running: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };
  return map[status] || 'bg-gray-100 text-gray-800';
};

// Estimativa de tempo total de envio baseada no delay configurado
const estimatedSendTime = computed(() => {
  const min = parseInt(form.value.delay_min) || 5;
  const max = parseInt(form.value.delay_max) || 12;
  const avg = (min + max) / 2;
  return { min, max, avg };
});

const resetForm = () => {
  form.value = {
    title: '',
    channel_type: 'whatsapp',
    inbox_id: '',
    message: '',
    scheduled_at: '',
    recipient_type: 'all',
    label_ids: [],
    date_from: '',
    date_to: '',
    delay_min: 5,
    delay_max: 12,
  };
  editingId.value = null;
};

const openNew = () => {
  resetForm();
  showNewForm.value = true;
};

const openEdit = bm => {
  form.value = {
    title: bm.title,
    channel_type: bm.channel_type || 'whatsapp',
    inbox_id: bm.inbox?.id || '',
    message: bm.message,
    scheduled_at: bm.scheduled_at ? bm.scheduled_at.substring(0, 16) : '',
    recipient_type: bm.recipient_type || 'all',
    label_ids: bm.label_ids || [],
    date_from: bm.date_from || '',
    date_to: bm.date_to || '',
    delay_min: bm.delay_min || 5,
    delay_max: bm.delay_max || 12,
  };
  editingId.value = bm.id;
  showNewForm.value = true;
};

const closeForm = () => {
  showNewForm.value = false;
  showLinkDialog.value = false;
  showImageUrlDialog.value = false;
  resetForm();
};

const saveForm = async () => {
  if (!form.value.title || !form.value.inbox_id || !form.value.message) return;
  try {
    const payload = {
      title: form.value.title,
      message: form.value.message,
      inbox_id: form.value.inbox_id,
      scheduled_at: form.value.scheduled_at || null,
      recipient_type: form.value.recipient_type || 'all',
      label_ids: form.value.recipient_type === 'label' ? (form.value.label_ids || []) : [],
      date_from: form.value.date_from || null,
      date_to: form.value.date_to || null,
      delay_min: parseInt(form.value.delay_min) || 5,
      delay_max: parseInt(form.value.delay_max) || 12,
    };
    if (editingId.value) {
      await store.dispatch('bulkMessages/update', { id: editingId.value, ...payload });
    } else {
      await store.dispatch('bulkMessages/create', payload);
    }
    closeForm();
  } catch (e) {
    // ignore
  }
};

const sendNow = async bm => {
  if (isSending.value) return;
  try {
    await store.dispatch('bulkMessages/sendNow', bm.id);
  } catch (e) {
    // ignore
  }
};

const deleteBulkMessage = async bm => {
  if (!confirm(`Eliminar "${bm.title}"?`)) return;
  try {
    await store.dispatch('bulkMessages/delete', bm.id);
  } catch (e) {
    // ignore
  }
};

// ─── Editor helpers ────────────────────────────────────────────────────────────

const insertAtCursor = (text) => {
  const el = messageTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const before = form.value.message.substring(0, start);
  const after = form.value.message.substring(end);
  form.value.message = before + text + after;
  nextTick(() => {
    el.focus();
    const pos = start + text.length;
    el.setSelectionRange(pos, pos);
  });
};

const insertVariable = (varKey) => {
  insertAtCursor(varKey);
};

const formatSelection = (prefix, suffix) => {
  const el = messageTextarea.value;
  if (!el) return;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const selected = form.value.message.substring(start, end);
  const before = form.value.message.substring(0, start);
  const after = form.value.message.substring(end);
  const replacement = selected
    ? `${prefix}${selected}${suffix}`
    : `${prefix}texto${suffix}`;
  form.value.message = before + replacement + after;
  nextTick(() => {
    el.focus();
    if (selected) {
      el.setSelectionRange(start, start + replacement.length);
    } else {
      const pos = start + prefix.length;
      el.setSelectionRange(pos, pos + 5);
    }
  });
};

const openLinkDialog = () => {
  const el = messageTextarea.value;
  if (el) {
    const selected = form.value.message.substring(el.selectionStart, el.selectionEnd);
    linkText.value = selected || '';
  }
  linkUrl.value = 'https://';
  showLinkDialog.value = true;
};

const confirmLink = () => {
  if (!linkUrl.value) return;
  const text = linkText.value || linkUrl.value;
  insertAtCursor(`[${text}](${linkUrl.value})`);
  showLinkDialog.value = false;
  linkUrl.value = '';
  linkText.value = '';
};

const triggerImageUpload = () => {
  imageFileInput.value?.click();
};

const handleImageFile = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingImage.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    insertAtCursor(`\n![imagem](${fileUrl})\n`);
  } catch (e) {
    // ignore
  } finally {
    isUploadingImage.value = false;
    if (imageFileInput.value) imageFileInput.value.value = '';
  }
};

const openImageUrlDialog = () => {
  imageUrlInput.value = 'https://';
  showImageUrlDialog.value = true;
};

const confirmImageUrl = () => {
  if (!imageUrlInput.value || imageUrlInput.value === 'https://') return;
  insertAtCursor(`\n![imagem](${imageUrlInput.value})\n`);
  showImageUrlDialog.value = false;
  imageUrlInput.value = '';
};

const messageCharCount = computed(() => form.value.message.length);

onMounted(() => {
  store.dispatch('bulkMessages/fetch');
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
});
</script>

<template>
  <CampaignLayout
    header-title="Envio em Massa"
    button-label="Nova Mensagem"
    @click="openNew"
    @close="closeForm"
  >
    <template #action>
      <!-- Form modal -->
      <div
        v-if="showNewForm"
        class="absolute right-0 top-12 z-50 w-[580px] bg-white dark:bg-n-solid-3 rounded-xl shadow-2xl border border-n-weak p-6 max-h-[92vh] overflow-y-auto"
      >
        <h3 class="text-heading-2 text-n-slate-12 mb-4">
          {{ editingId ? 'Editar Envio em Massa' : 'Nova Mensagem em Massa' }}
        </h3>

        <!-- Título -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Título</label>
          <input
            v-model="form.title"
            type="text"
            placeholder="Ex: Promoção de Verão"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <!-- Canal -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Canal</label>
          <select
            v-model="form.channel_type"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
            @change="form.inbox_id = ''; form.recipient_type = 'all'"
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>
        </div>

        <!-- Caixa de entrada -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Caixa de Entrada</label>
          <select
            v-model="form.inbox_id"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option value="">Seleccionar caixa de entrada...</option>
            <option v-for="inbox in availableInboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Destinatários -->
        <div class="mb-3">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Destinatários</label>
          <select
            v-model="form.recipient_type"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          >
            <option
              v-for="opt in recipientOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
          <p
            v-if="selectedRecipientDescription"
            class="mt-1 text-xs text-n-slate-9 flex items-center gap-1"
          >
            <span class="i-lucide-info text-blue-500" />
            {{ selectedRecipientDescription }}
          </p>
        </div>

        <!-- Aviso Fora de 24h -->
        <div
          v-if="form.recipient_type === 'window_outside'"
          class="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
        >
          <p class="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
            <span class="i-lucide-triangle-alert shrink-0 mt-0.5" />
            <span>
              Estes contactos estão <strong>fora da janela de 24h</strong>.
              O WhatsApp cobra por mensagem enviada fora desta janela.
              Certifique-se de que tem um <strong>template aprovado</strong> configurado.
            </span>
          </p>
        </div>

        <!-- Aviso Dentro de 24h -->
        <div
          v-if="form.recipient_type === 'window_inside'"
          class="mb-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700"
        >
          <p class="text-xs text-green-800 dark:text-green-300 flex items-start gap-2">
            <span class="i-lucide-check-circle shrink-0 mt-0.5" />
            <span>
              Estes contactos iniciaram conversa nas <strong>últimas 24h</strong>.
              Pode enviar mensagens <strong>sem custo adicional</strong>.
            </span>
          </p>
        </div>

        <!-- Etiquetas -->
        <div v-if="form.recipient_type === 'label'" class="mb-3">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">Etiquetas</label>
          <div class="flex flex-wrap gap-2 p-2 border border-n-weak rounded-lg bg-n-surface-1 max-h-28 overflow-y-auto">
            <label
              v-for="label in labels"
              :key="label.id"
              class="flex items-center gap-1 text-xs cursor-pointer"
            >
              <input
                v-model="form.label_ids"
                type="checkbox"
                :value="label.id"
                class="rounded"
              />
              <span
                class="px-2 py-0.5 rounded-full text-white text-xs"
                :style="{ backgroundColor: label.color || '#6b7280' }"
              >
                {{ label.title }}
              </span>
            </label>
          </div>
        </div>

        <!-- ─── Filtro de Data ──────────────────────────────────────────────── -->
        <div class="mb-3 p-3 rounded-lg border border-n-weak bg-n-surface-1">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-calendar-search text-woot-500 text-sm" />
            <span class="text-sm font-medium text-n-slate-11">Filtro por data de actividade</span>
            <span class="text-xs text-n-slate-9 ml-auto">(opcional)</span>
          </div>
          <p class="text-xs text-n-slate-9 mb-2">
            Filtra contactos que tiveram conversas nesta inbox dentro do período indicado.
            Deixe em branco para não filtrar por data.
          </p>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">De (data inicial)</label>
              <input
                v-model="form.date_from"
                type="date"
                class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Até (data final)</label>
              <input
                v-model="form.date_to"
                type="date"
                class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
            </div>
          </div>
          <!-- Atalhos de data rápidos -->
          <div class="flex flex-wrap gap-1.5 mt-2">
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              @click="() => {
                const today = new Date().toISOString().split('T')[0];
                form.date_from = today; form.date_to = today;
              }"
            >Hoje</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              @click="() => {
                const d = new Date(); d.setDate(d.getDate() - 7);
                form.date_from = d.toISOString().split('T')[0];
                form.date_to = new Date().toISOString().split('T')[0];
              }"
            >Últimos 7 dias</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              @click="() => {
                const d = new Date(); d.setDate(d.getDate() - 30);
                form.date_from = d.toISOString().split('T')[0];
                form.date_to = new Date().toISOString().split('T')[0];
              }"
            >Últimos 30 dias</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              @click="() => { form.date_from = ''; form.date_to = ''; }"
            >Limpar</button>
          </div>
        </div>

        <!-- ─── Delay entre mensagens ──────────────────────────────────────── -->
        <div class="mb-3 p-3 rounded-lg border border-n-weak bg-n-surface-1">
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-timer text-woot-500 text-sm" />
            <span class="text-sm font-medium text-n-slate-11">Intervalo entre mensagens</span>
          </div>
          <p class="text-xs text-n-slate-9 mb-2">
            Um delay aleatório entre <strong>{{ form.delay_min }}s</strong> e <strong>{{ form.delay_max }}s</strong>
            é aplicado entre cada envio para simular comportamento humano e reduzir o risco de banimento.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Mínimo (segundos)</label>
              <input
                v-model.number="form.delay_min"
                type="number"
                min="1"
                max="300"
                class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-10 mb-1">Máximo (segundos)</label>
              <input
                v-model.number="form.delay_max"
                type="number"
                min="1"
                max="600"
                class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              />
            </div>
          </div>
          <!-- Presets de delay -->
          <div class="flex flex-wrap gap-1.5 mt-2">
            <span class="text-xs text-n-slate-9 mr-1">Presets:</span>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              :class="form.delay_min === 3 && form.delay_max === 7 ? 'bg-woot-50 border-woot-300 text-woot-700' : ''"
              @click="form.delay_min = 3; form.delay_max = 7"
            >Rápido (3–7s)</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              :class="form.delay_min === 5 && form.delay_max === 12 ? 'bg-woot-50 border-woot-300 text-woot-700' : ''"
              @click="form.delay_min = 5; form.delay_max = 12"
            >Normal (5–12s)</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              :class="form.delay_min === 10 && form.delay_max === 25 ? 'bg-woot-50 border-woot-300 text-woot-700' : ''"
              @click="form.delay_min = 10; form.delay_max = 25"
            >Seguro (10–25s)</button>
            <button
              type="button"
              class="px-2 py-0.5 text-xs rounded border border-n-weak text-n-slate-10 hover:bg-n-surface-2 transition-colors"
              :class="form.delay_min === 30 && form.delay_max === 60 ? 'bg-woot-50 border-woot-300 text-woot-700' : ''"
              @click="form.delay_min = 30; form.delay_max = 60"
            >Muito seguro (30–60s)</button>
          </div>
        </div>

        <!-- ─── Variáveis disponíveis ──────────────────────────────────────── -->
        <div class="mb-3 p-3 rounded-lg border border-n-weak bg-n-surface-1">
          <p class="text-xs font-semibold text-n-slate-11 mb-2 flex items-center gap-1">
            <span class="i-lucide-braces text-woot-500" />
            Variáveis disponíveis
            <span class="text-n-slate-9 font-normal ml-1">— clique para inserir</span>
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="variable in availableVariables"
              :key="variable.key"
              type="button"
              :title="variable.description"
              class="px-2 py-1 text-xs rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 border border-woot-200 dark:border-woot-700 hover:bg-woot-100 dark:hover:bg-woot-800/40 transition-colors font-mono"
              @click="insertVariable(variable.key)"
            >
              {{ variable.key }}
            </button>
          </div>
          <div class="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
            <div
              v-for="variable in availableVariables"
              :key="variable.key + '_desc'"
              class="flex items-center gap-1 text-xs text-n-slate-9"
            >
              <span class="font-mono text-woot-600 dark:text-woot-400">{{ variable.key }}</span>
              <span>— {{ variable.description }}</span>
            </div>
          </div>
        </div>

        <!-- ─── Editor de Mensagem ─────────────────────────────────────────── -->
        <div class="mb-3">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-sm font-medium text-n-slate-11">
              Mensagem
              <span class="text-xs text-n-slate-9 font-normal ml-1">Use , para personalizar</span>
            </label>
            <span class="text-xs text-n-slate-9">{{ messageCharCount }} caracteres</span>
          </div>

          <!-- Barra de ferramentas -->
          <div class="flex items-center gap-0.5 px-2 py-1.5 border border-b-0 border-n-weak rounded-t-lg bg-n-surface-2">
            <!-- Negrito -->
            <button
              type="button"
              title="Negrito (*texto*)"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors font-bold text-sm"
              @click="formatSelection('*', '*')"
            >B</button>
            <!-- Itálico -->
            <button
              type="button"
              title="Itálico (_texto_)"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors italic text-sm"
              @click="formatSelection('_', '_')"
            >I</button>
            <!-- Riscado -->
            <button
              type="button"
              title="Riscado (~texto~)"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors"
              @click="formatSelection('~', '~')"
            >
              <span class="i-lucide-strikethrough text-sm" />
            </button>
            <!-- Código -->
            <button
              type="button"
              title="Código (`texto`)"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors"
              @click="formatSelection('`', '`')"
            >
              <span class="i-lucide-code text-sm" />
            </button>

            <div class="w-px h-4 bg-n-weak mx-1" />

            <!-- Link -->
            <button
              type="button"
              title="Inserir link"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors"
              @click="openLinkDialog"
            >
              <span class="i-lucide-link text-sm" />
            </button>

            <div class="w-px h-4 bg-n-weak mx-1" />

            <!-- Upload de imagem por ficheiro -->
            <button
              type="button"
              title="Carregar imagem do computador"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors relative"
              :disabled="isUploadingImage"
              @click="triggerImageUpload"
            >
              <span v-if="isUploadingImage" class="i-lucide-loader-2 text-sm animate-spin" />
              <span v-else class="i-lucide-image-plus text-sm" />
            </button>
            <!-- Upload de imagem por URL -->
            <button
              type="button"
              title="Inserir imagem por URL"
              class="p-1.5 rounded hover:bg-n-surface-3 text-n-slate-11 transition-colors"
              @click="openImageUrlDialog"
            >
              <span class="i-lucide-link-2 text-sm" />
            </button>

            <div class="w-px h-4 bg-n-weak mx-1" />

            <span class="text-xs text-n-slate-9 ml-auto">Markdown suportado</span>
          </div>

          <!-- Textarea -->
          <textarea
            ref="messageTextarea"
            v-model="form.message"
            rows="6"
            placeholder="Olá {{nome}}, temos uma novidade para si...

Use a barra acima para formatar ou clique nas variáveis para personalizar."
            class="w-full px-3 py-2 rounded-b-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500 resize-y font-mono"
          />

          <!-- Input oculto para upload de imagem -->
          <input
            ref="imageFileInput"
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            class="hidden"
            @change="handleImageFile"
          />
        </div>

        <!-- ─── Dialog: Inserir Link ──────────────────────────────────────── -->
        <div
          v-if="showLinkDialog"
          class="mb-3 p-3 rounded-lg border border-woot-200 dark:border-woot-700 bg-woot-50 dark:bg-woot-900/20"
        >
          <p class="text-xs font-semibold text-n-slate-11 mb-2 flex items-center gap-1">
            <span class="i-lucide-link text-woot-500" />
            Inserir link
          </p>
          <div class="flex flex-col gap-2">
            <input
              v-model="linkText"
              type="text"
              placeholder="Texto do link (opcional)"
              class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
            />
            <input
              v-model="linkUrl"
              type="url"
              placeholder="https://exemplo.com"
              class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              @keyup.enter="confirmLink"
            />
            <div class="flex gap-2 justify-end">
              <button
                type="button"
                class="px-3 py-1 text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-surface-2"
                @click="showLinkDialog = false"
              >Cancelar</button>
              <button
                type="button"
                class="px-3 py-1 text-xs rounded bg-woot-500 text-white hover:bg-woot-600"
                @click="confirmLink"
              >Inserir</button>
            </div>
          </div>
        </div>

        <!-- ─── Dialog: Inserir Imagem por URL ───────────────────────────── -->
        <div
          v-if="showImageUrlDialog"
          class="mb-3 p-3 rounded-lg border border-woot-200 dark:border-woot-700 bg-woot-50 dark:bg-woot-900/20"
        >
          <p class="text-xs font-semibold text-n-slate-11 mb-2 flex items-center gap-1">
            <span class="i-lucide-image text-woot-500" />
            Inserir imagem por URL
          </p>
          <div class="flex flex-col gap-2">
            <input
              v-model="imageUrlInput"
              type="url"
              placeholder="https://exemplo.com/imagem.jpg"
              class="w-full px-2 py-1.5 rounded border border-n-weak bg-n-surface-1 text-n-slate-12 text-xs focus:outline-none focus:ring-1 focus:ring-woot-500"
              @keyup.enter="confirmImageUrl"
            />
            <div class="flex gap-2 justify-end">
              <button
                type="button"
                class="px-3 py-1 text-xs rounded border border-n-weak text-n-slate-11 hover:bg-n-surface-2"
                @click="showImageUrlDialog = false"
              >Cancelar</button>
              <button
                type="button"
                class="px-3 py-1 text-xs rounded bg-woot-500 text-white hover:bg-woot-600"
                @click="confirmImageUrl"
              >Inserir</button>
            </div>
          </div>
        </div>

        <!-- Agendamento -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            Agendamento
            <span class="text-xs text-n-slate-9 font-normal ml-1">(opcional — deixe vazio para enviar agora)</span>
          </label>
          <input
            v-model="form.scheduled_at"
            type="datetime-local"
            class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
          />
        </div>

        <!-- Botões -->
        <div class="flex justify-end gap-2">
          <Button
            variant="clear"
            label="Cancelar"
            size="sm"
            @click="closeForm"
          />
          <Button
            label="Guardar"
            size="sm"
            :disabled="!form.title || !form.inbox_id || !form.message"
            @click="saveForm"
          />
        </div>
      </div>
    </template>

    <!-- Loading -->
    <div v-if="isFetching" class="flex items-center justify-center py-10 text-n-slate-11">
      <Spinner />
    </div>

    <!-- Lista vazia -->
    <div
      v-else-if="allBulkMessages.length === 0"
      class="flex flex-col items-center justify-center py-20 text-n-slate-9"
    >
      <span class="i-lucide-send text-5xl mb-4 opacity-30" />
      <p class="text-lg font-medium">Nenhum envio em massa criado</p>
      <p class="text-sm mt-1">Clique em "Nova Mensagem" para começar</p>
    </div>

    <!-- Tabela de envios -->
    <div v-else class="space-y-3">
      <div
        v-for="bm in allBulkMessages"
        :key="bm.id"
        class="flex items-center justify-between p-4 rounded-xl border border-n-weak bg-n-surface-1 hover:bg-n-surface-2 transition-colors"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span class="font-medium text-n-slate-12 truncate">{{ bm.title }}</span>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-medium"
              :class="statusClass(bm.status)"
            >
              {{ statusLabel(bm.status) }}
            </span>
          </div>
          <div class="flex items-center flex-wrap gap-3 text-xs text-n-slate-9">
            <span class="flex items-center gap-1">
              <span class="i-lucide-message-circle" />
              {{ bm.channel_type === 'whatsapp' ? 'WhatsApp' : 'Email' }}
            </span>
            <span class="flex items-center gap-1">
              <span class="i-lucide-users" />
              {{ recipientLabel(bm.recipient_type) }}
            </span>
            <!-- Filtro de data se definido -->
            <span v-if="bm.date_from || bm.date_to" class="flex items-center gap-1 text-blue-600">
              <span class="i-lucide-calendar" />
              <span v-if="bm.date_from && bm.date_to">{{ bm.date_from }} → {{ bm.date_to }}</span>
              <span v-else-if="bm.date_from">desde {{ bm.date_from }}</span>
              <span v-else>até {{ bm.date_to }}</span>
            </span>
            <!-- Delay configurado -->
            <span v-if="bm.delay_min || bm.delay_max" class="flex items-center gap-1 text-purple-600">
              <span class="i-lucide-timer" />
              {{ bm.delay_min || 5 }}–{{ bm.delay_max || 12 }}s
            </span>
            <span v-if="bm.total_recipients" class="flex items-center gap-1">
              {{ bm.total_recipients }} destinatários
            </span>
            <span v-if="bm.sent_count" class="flex items-center gap-1 text-green-600">
              <span class="i-lucide-check" />
              {{ bm.sent_count }} enviados
            </span>
            <span v-if="bm.failed_count" class="flex items-center gap-1 text-red-600">
              <span class="i-lucide-x" />
              {{ bm.failed_count }} falharam
            </span>
            <span v-if="bm.scheduled_at" class="flex items-center gap-1">
              <span class="i-lucide-clock" />
              {{ new Date(bm.scheduled_at).toLocaleString('pt-BR') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-9 mt-1 truncate max-w-md">{{ bm.message }}</p>
        </div>

        <div class="flex items-center gap-2 ml-4 shrink-0">
          <Button
            v-if="bm.status === 'draft'"
            variant="clear"
            icon="i-lucide-pencil"
            size="xs"
            @click="openEdit(bm)"
          />
          <Button
            v-if="bm.status === 'draft'"
            icon="i-lucide-send"
            label="Enviar"
            size="xs"
            :disabled="isSending"
            @click="sendNow(bm)"
          />
          <Button
            v-if="['draft', 'failed', 'completed'].includes(bm.status)"
            variant="clear"
            icon="i-lucide-trash-2"
            size="xs"
            color-scheme="alert"
            @click="deleteBulkMessage(bm)"
          />
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
